import React, { useState, useRef, useEffect } from 'react';
import OpenAI from 'openai';
import '../AIMChat.css';
import { supabase } from '../supabase';

const ChatInterface = ({ neobot, user }) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const messagesEndRef = useRef(null);

  const openai = new OpenAI({
    apiKey: process.env.REACT_APP_OPENAI_API_KEY,
    dangerouslyAllowBrowser: true
  });

  // Load messages from Supabase when neobot changes
  useEffect(() => {
    if (neobot && user) {
      loadMessages();
    } else {
      setMessages([]);
      setLoadingMessages(false);
    }
  }, [neobot, user]);

  const loadMessages = async () => {
    if (!neobot || !user) return;
    
    setLoadingMessages(true);
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('user_id', user.id)
        .eq('bot_id', neobot.id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      setMessages(data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const saveMessage = async (messageData) => {
    if (!user || !neobot) return;

    try {
      const { data, error } = await supabase
        .from('messages')
        .insert([{
          user_id: user.id,
          bot_id: neobot.id,
          role: messageData.role,
          content: messageData.content,
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving message:', error);
      throw error;
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || !user) return;

    setIsLoading(true);
    const userMessage = { role: 'user', content: input };
    setInput('');

    try {
      // Save user message to database
      await saveMessage(userMessage);
      setMessages(prev => [...prev, userMessage]);

      // Create messages array with system prompt
      const allMessages = [
        { role: 'system', content: neobot.systemPrompt },
        ...messages,
        userMessage
      ];

      const completion = await openai.chat.completions.create({
        model: "gpt-3.5-turbo",
        messages: allMessages,
        max_tokens: 500,
      });

      const assistantMessage = {
        role: 'assistant',
        content: completion.choices[0].message.content
      };
      
      // Save assistant message to database
      await saveMessage(assistantMessage);
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = async () => {
    if (!user || !neobot) return;

    try {
      const { error } = await supabase
        .from('messages')
        .delete()
        .eq('user_id', user.id)
        .eq('bot_id', neobot.id);

      if (error) throw error;

      setMessages([]);
    } catch (error) {
      console.error('Error clearing chat:', error);
      alert('Failed to clear chat. Please try again.');
    }
  };

  if (loadingMessages) {
    return (
      <div className="aim-chat-container">
        <div className="aim-chat-header" style={{ display: 'flex', alignItems: 'center' }}>
          <span className="aim-status-indicator aim-status-online"></span>
          {neobot.avatarUrl && (
            <img 
              src={neobot.avatarUrl} 
              alt={neobot.name}
              style={{ 
                width: '28px', 
                height: '28px', 
                borderRadius: '50%', 
                marginRight: '8px',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}
            />
          )}
          {neobot.name}
        </div>
        <div className="aim-messages-area" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div className="aim-system-message">
            Loading chat history...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="aim-chat-container" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="aim-chat-header" style={{ display: 'flex', alignItems: 'center' }}>
        <span className="aim-status-indicator aim-status-online"></span>
        {neobot.avatarUrl && (
          <img 
            src={neobot.avatarUrl} 
            alt={neobot.name}
            style={{ 
              width: '28px', 
              height: '28px', 
              borderRadius: '50%', 
              marginRight: '8px',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}
          />
        )}
        {neobot.name}
        <button 
            className="aim-button" 
            onClick={clearChat}
            style={{ fontSize: '10px', padding: '2px 6px', marginLeft: 'auto' }}
            title="Clear chat history"
          >
            Clear
          </button>
      </div>
      
      <div className="aim-messages-area" style={{ flex: 1, overflow: 'auto' }}>
        {messages.length === 0 && (
          <div className="aim-system-message">
            Start chatting with {neobot.name}!
          </div>
        )}
        
        {messages.map((message, index) => (
          <div key={index} className="aim-message">
            <div className="aim-message-sender">
              {message.role === 'user' ? 'You' : neobot.name}
            </div>
            <div className="aim-message-text">
              {message.content}
            </div>
            <div className="aim-message-time">
              {new Date(message.created_at || Date.now()).toLocaleTimeString()}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="aim-typing-indicator">
            {neobot.name} is typing...
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      <div className="aim-input-area">
        <input
          type="text"
          className="aim-text-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={`Message ${neobot.name}...`}
          disabled={isLoading}
        />
        <button 
          onClick={sendMessage} 
          disabled={isLoading || !input.trim()}
          className="aim-send-button"
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default ChatInterface; 